import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Button } from './components/ui/button';
import {
  ArrowRight, Mail, CalendarClock, Crown, Dumbbell, Apple,
  Trophy, HeartHandshake, BarChart3, Sparkles, Users, CheckCircle2,
  Instagram, CreditCard, MessageSquare, Maximize2, X, ChevronLeft, ChevronRight,
  Gift, Lock, TrendingDown, Info,
  Globe, Smartphone, Rocket, Megaphone, Target, Repeat, ExternalLink, FileText
} from 'lucide-react';

// ── Single source of truth for investor contact / links ─────────────────────
// Replace these values only; no layout changes required.
const INVESTOR = {
  email: 'info@onlymass.net',
  bookingUrl: 'https://calendly.com/onlymass-info/onlymass-investor-meeting',
  deckUrl: '/invest/OnlyMass-Investor-Deck-2026.pdf',
  round: 'Preparing a €250K Pre-Seed Round',
};

const PAGE_META = {
  title: 'OnlyMass — Investor Overview | €250K Pre-Seed',
  description: 'OnlyMass is the monetisation platform built for fitness creators. Web platform live, mobile app approaching launch. Preparing a €250K pre-seed round.',
};

// ── Analytics (PostHog snippet is already loaded in public/index.html) ──────
const useInvestTracking = () => {
  const ref = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('ref') || null;
    } catch { return null; }
  }, []);

  const track = useCallback((event, props = {}) => {
    try {
      window.posthog?.capture?.(event, { ref, page: '/invest', ...props });
    } catch { /* analytics must never break the page */ }
  }, [ref]);

  return { track, ref };
};

// ── Small building blocks ───────────────────────────────────────────────────
const Eyebrow = ({ children }) => (
  <p className="text-xs sm:text-sm font-semibold text-blue-600 uppercase tracking-[0.14em] mb-3">{children}</p>
);

const EyebrowLight = ({ children }) => (
  <p className="text-xs sm:text-sm font-semibold text-blue-300 uppercase tracking-[0.14em] mb-3">{children}</p>
);

const Section = ({ id, dark, tint, children, testId }) => (
  <section
    id={id}
    data-testid={testId}
    className={dark ? 'py-16 sm:py-24 bg-[#0f1d3d]' : tint ? 'py-16 sm:py-24 bg-slate-50' : 'py-16 sm:py-24 bg-white'}
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">{children}</div>
  </section>
);

// ── Real product screenshots (captured from the live OnlyMass web platform) ─
// Add/replace entries here; the layout adapts automatically.
const SCREENS = [
  { src: '/invest/screen-plan-builder.webp', kind: 'desktop', title: 'Programme Builder', caption: 'Access level, pricing model and per-exercise demo videos' },
  { src: '/invest/screen-meal-plan-builder.webp', kind: 'framed', title: 'Meal Plan Builder', caption: 'Creators can configure access, one-time pricing, nutrition categories, calories, protein targets and detailed meal-plan structure.' },
  { src: '/invest/screen-library-challenges-mobile.webp', kind: 'mobile', title: 'Library & Challenges', caption: 'Saved and purchased programmes, community challenges and rewards in the mobile app.' },
  { src: '/invest/screen-challenge-rewards.webp', kind: 'framed', title: 'Challenge Rewards', caption: 'Creators can reward top participants with subscriptions, workout plans, meal plans, featured placement or achievement badges.' },
  { src: '/invest/screen-professional-mode-mobile.webp', kind: 'mobile', title: 'Creator Professional Mode', caption: 'Stripe-connected payments, payouts, platform fee visibility, subscribers and creator earnings controls.' },
  { src: '/invest/screen-badges.webp', kind: 'desktop', title: 'Gamification', caption: 'Levels, XP, streaks and a 37-badge achievement system' },
  { src: '/invest/screen-ai-coach.webp', kind: 'desktop', title: 'AI fitness coach', caption: 'Goal-based personalised recommendations' },
];

const ScreenCard = ({ item, index, onOpen }) => {
  const isMobile = item.kind === 'mobile';
  // 'framed' images already include their own browser chrome, so no extra bar.
  const isFramed = item.kind === 'framed';
  return (
    <figure className="group">
      <button type="button" onClick={() => onOpen(index)} aria-label={`Enlarge screenshot: ${item.title}`}
        data-testid={`invest-screenshot-${index}`}
        className="block w-full text-left cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-2xl">
        <div className="relative rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm group-hover:shadow-md group-hover:border-blue-300 transition-all">
          {isFramed ? (
            <div className="flex items-start justify-center bg-white p-3">
              <img src={item.src} alt={item.title} loading="lazy" decoding="async"
                className="w-full max-h-[320px] object-contain" />
            </div>
          ) : isMobile ? (
            <>
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                  <Smartphone className="h-3 w-3" /> Mobile app
                </span>
              </div>
              <div className="flex items-start justify-center bg-white px-4 pt-4">
                <img src={item.src} alt={item.title} loading="lazy" decoding="async"
                  className="w-auto max-w-full max-h-[420px] object-contain" />
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50">
                <span className="w-2 h-2 rounded-full bg-slate-300" />
                <span className="w-2 h-2 rounded-full bg-slate-300" />
                <span className="w-2 h-2 rounded-full bg-slate-300" />
              </div>
              <div className="max-h-[320px] overflow-hidden bg-white">
                <img src={item.src} alt={item.title} loading="lazy" decoding="async"
                  className="w-full object-cover object-top" />
              </div>
            </>
          )}
          <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-[#0f1d3d]/85 backdrop-blur px-2.5 py-1 text-[11px] font-semibold text-white sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            <Maximize2 className="h-3 w-3" /> Tap to enlarge
          </span>
        </div>
      </button>
      <figcaption className="mt-3">
        <p className="text-sm font-semibold text-[#0f1d3d]">{item.title}</p>
        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.caption}</p>
      </figcaption>
    </figure>
  );
};

const ScreenLightbox = ({ index, onClose, onStep }) => {
  const item = SCREENS[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onStep(1);
      if (e.key === 'ArrowLeft') onStep(-1);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onStep]);

  return (
    <div role="dialog" aria-modal="true" aria-label={item.title} data-testid="invest-lightbox"
      className="fixed inset-0 z-[60] bg-[#0b1528]/95 backdrop-blur-sm flex flex-col animate-in fade-in duration-200"
      onClick={onClose}>
      <div className="flex items-start justify-between gap-4 px-4 sm:px-6 pt-4 pb-3 shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">{item.title}</p>
          <p className="text-xs text-blue-200/60 mt-0.5">{item.caption}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close screenshot"
          data-testid="invest-lightbox-close"
          className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white text-[#0f1d3d] text-sm font-semibold active:scale-[0.97] transition-transform">
          <X className="h-4 w-4" /> Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 pb-4"
        onClick={(e) => e.stopPropagation()}>
        <img src={item.src} alt={item.title}
          className={`mx-auto rounded-xl border border-white/10 bg-white shadow-2xl ${item.kind === 'mobile' ? 'w-auto max-w-full max-h-full' : 'w-full max-w-4xl'}`} />
      </div>

      <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-white/10"
        onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => onStep(-1)} aria-label="Previous screenshot"
          data-testid="invest-lightbox-prev"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors">
          <ChevronLeft className="h-4 w-4" /> Prev
        </button>
        <span className="text-xs text-blue-200/50">{index + 1} / {SCREENS.length}</span>
        <button type="button" onClick={() => onStep(1)} aria-label="Next screenshot"
          data-testid="invest-lightbox-next"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors">
          Next <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const ProductScreens = ({ onOpenScreen, openIndex, onClose, onStep }) => (
  <div data-testid="invest-screenshots">
    <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
      {SCREENS.map((item, i) => (
        <ScreenCard key={i} item={item} index={i} onOpen={onOpenScreen} />
      ))}
    </div>
    <p className="mt-8 text-xs text-slate-400 max-w-2xl">
      Screenshots from the OnlyMass web platform and mobile app, shown to demonstrate product
      functionality only — they do not represent customer traction, revenue or performance. Tap any
      screenshot to enlarge.
    </p>
    {openIndex !== null && (
      <ScreenLightbox index={openIndex} onClose={onClose} onStep={onStep} />
    )}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// INVEST PAGE
// ═══════════════════════════════════════════════════════════════════════════
const InvestPage = () => {
  const { track, ref } = useInvestTracking();
  const [screenIndex, setScreenIndex] = useState(null);

  // Investor-specific metadata, scoped to this route only.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_META.title;

    const created = [];
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        const prev = el.getAttribute('content');
        created.push(() => el.setAttribute('content', prev ?? ''));
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(() => el.remove());
      }
      el.setAttribute('content', content);
    };

    setMeta('name', 'robots', 'noindex, follow');
    setMeta('name', 'description', PAGE_META.description);
    setMeta('property', 'og:title', PAGE_META.title);
    setMeta('property', 'og:description', PAGE_META.description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', `${window.location.origin}/invest`);
    setMeta('property', 'og:image', `${window.location.origin}/brand/icon-512.png`);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', PAGE_META.title);
    setMeta('name', 'twitter:description', PAGE_META.description);

    return () => {
      document.title = prevTitle;
      created.forEach((undo) => undo());
    };
  }, []);

  useEffect(() => { track('invest_page_view'); }, [track]);

  const contactHref = `mailto:${INVESTOR.email}?subject=OnlyMass%20Investment%20Inquiry`;

  const handleContact = (source) => track('invest_contact_click', { source });
  const handleBooking = (source) => track('invest_booking_click', { source });
  const handleDeck = (source) => track('invest_deck_click', { source });

  const openScreen = (i) => {
    setScreenIndex(i);
    track('invest_screenshot_open', { screen: SCREENS[i]?.title });
  };
  const closeScreen = () => setScreenIndex(null);
  const stepScreen = (dir) =>
    setScreenIndex((prev) => (prev === null ? prev : (prev + dir + SCREENS.length) % SCREENS.length));

  const scrollToProduct = () => {
    track('invest_product_click');
    document.getElementById('product')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white antialiased">
      {/* Nav */}
      <nav className="bg-white/85 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <RouterLink to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity" data-testid="invest-nav-logo">
              <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-bold text-[#0f1d3d] tracking-tight">OnlyMass</span>
            </RouterLink>
            <span className="text-[11px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">Investor Overview</span>
          </div>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#0f1d3d] pt-14 pb-16 sm:pt-20 sm:pb-24" data-testid="invest-hero">
        <div className="absolute inset-0 opacity-[0.5]" aria-hidden="true"
          style={{ background: 'radial-gradient(60% 55% at 15% 0%, rgba(46,169,217,0.28) 0%, rgba(15,29,61,0) 70%)' }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3 py-1.5 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[11px] sm:text-xs font-semibold text-blue-100 tracking-wide" data-testid="invest-round-label">
                {INVESTOR.round}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
              The monetisation platform built for fitness creators.
            </h1>

            <p className="mt-5 text-base md:text-lg text-blue-100/70 leading-relaxed max-w-2xl">
              Creators already have the audience. OnlyMass gives them the infrastructure to turn it into a business.
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs sm:text-sm text-blue-200/80" data-testid="invest-status">
              <span className="inline-flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" /> Web platform live</span>
              <span className="text-white/20">•</span>
              <span className="inline-flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" /> Mobile app approaching launch</span>
              <span className="text-white/20">•</span>
              <span>Europe</span>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button onClick={scrollToProduct} data-testid="invest-hero-product-btn"
                className="bg-white text-[#0f1d3d] hover:bg-blue-50 font-semibold h-12 px-6 text-base rounded-xl">
                View Product <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a href={INVESTOR.deckUrl} target="_blank" rel="noopener noreferrer"
                onClick={() => handleDeck('hero')} data-testid="invest-hero-deck-btn"
                className="inline-flex items-center justify-center h-12 px-6 rounded-xl border border-white/25 text-base font-semibold text-white hover:bg-white/10 transition-colors">
                <FileText className="mr-2 h-4 w-4" /> View Investor Deck
              </a>
              <a href={INVESTOR.bookingUrl} target="_blank" rel="noopener noreferrer"
                onClick={() => handleBooking('hero')} data-testid="invest-hero-booking-btn"
                className="inline-flex items-center justify-center h-12 px-6 rounded-xl border border-white/25 text-base font-semibold text-white hover:bg-white/10 transition-colors">
                <CalendarClock className="mr-2 h-4 w-4" /> Book a Meeting
              </a>
              <a href={contactHref} onClick={() => handleContact('hero')} data-testid="invest-hero-contact-btn"
                className="inline-flex items-center justify-center h-12 px-6 rounded-xl text-base font-semibold text-blue-200 hover:text-white transition-colors">
                <Mail className="mr-2 h-4 w-4" /> Contact Founder
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Problem ──────────────────────────────────────────────────────── */}
      <Section testId="invest-problem">
        <Eyebrow>The Problem</Eyebrow>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1d3d] leading-tight max-w-2xl">
          Fitness creators built audiences. They didn't build infrastructure.
        </h2>
        <p className="mt-4 text-sm md:text-base text-slate-500 leading-relaxed max-w-2xl">
          A creator's business today is spread across five disconnected tools. Nothing is joined up — not the
          content, not the payments, not the community, not the results.
        </p>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { icon: Instagram, label: 'Social media', note: 'Audience lives here' },
            { icon: FileText, label: 'Programmes', note: 'PDFs & spreadsheets' },
            { icon: CreditCard, label: 'Payments', note: 'Ad-hoc links' },
            { icon: MessageSquare, label: 'Community', note: 'Group chats' },
            { icon: Users, label: 'Coaching', note: 'DMs & email' },
          ].map((it, i) => (
            <div key={i} className="relative p-4 sm:p-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50/60">
              <it.icon className="h-5 w-5 text-slate-400 mb-3" />
              <p className="text-sm font-semibold text-[#0f1d3d] leading-tight">{it.label}</p>
              <p className="text-xs text-slate-400 mt-1">{it.note}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-slate-500 max-w-2xl">
          The result: revenue leaks, no retention, no data, and a business that stops the moment the creator stops posting.
        </p>
      </Section>

      {/* ── Solution / Product ───────────────────────────────────────────── */}
      <Section id="product" tint testId="invest-solution">
        <div>
          <div className="max-w-2xl">
            <Eyebrow>The Solution</Eyebrow>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1d3d] leading-tight">
              One platform. Built around the fitness creator business.
            </h2>
            <p className="mt-4 text-sm md:text-base text-slate-500 leading-relaxed">
              OnlyMass replaces the stack with a single operating system for the fitness creator — content,
              monetisation, community and progress in one place.
            </p>
          </div>

          <div>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { icon: Crown, title: 'Subscriptions', desc: 'Recurring creator memberships' },
                { icon: Dumbbell, title: 'Workout Programs', desc: 'Structured, trackable training' },
                { icon: Apple, title: 'Nutrition Plans', desc: 'Meals, macros, prep' },
                { icon: Trophy, title: 'Challenges', desc: 'Time-boxed community goals' },
                { icon: HeartHandshake, title: 'Tips & Premium Content', desc: 'One-off monetisation' },
                { icon: BarChart3, title: 'Creator Analytics', desc: 'Revenue and audience insight' },
                { icon: Sparkles, title: 'AI Fitness Tools', desc: 'Assisted programming' },
                { icon: Trophy, title: 'Gamification', desc: 'Streaks, badges, progress' },
              ].map((it, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-slate-200/80 hover:border-blue-300 hover:shadow-sm transition-colors">
                  <it.icon className="h-[18px] w-[18px] text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#0f1d3d] leading-tight">{it.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{it.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <a href="/" target="_blank" rel="noopener noreferrer"
              onClick={() => track('invest_live_product_click')} data-testid="invest-live-product-link"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
              Open live platform <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          <div className="mt-14">
            <p className="text-sm font-semibold text-[#0f1d3d] mb-6">Inside the product</p>
            <ProductScreens
              onOpenScreen={openScreen}
              openIndex={screenIndex}
              onClose={closeScreen}
              onStep={stepScreen}
            />
          </div>
        </div>
      </Section>

      {/* ── Two-sided value ──────────────────────────────────────────────── */}
      <Section dark testId="invest-two-sided">
        <EyebrowLight>Two-Sided Value</EyebrowLight>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight max-w-2xl">
          Creators get a business. Users get a result.
        </h2>

        <div className="mt-10 grid md:grid-cols-2 gap-4">
          <div className="p-6 sm:p-8 rounded-2xl bg-white/[0.06] border border-white/10">
            <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-4">For Creators</p>
            <div className="flex flex-wrap items-center gap-2 mb-5 text-sm font-semibold text-white">
              <span>Audience</span><ArrowRight className="h-3.5 w-3.5 text-blue-400" />
              <span>Community</span><ArrowRight className="h-3.5 w-3.5 text-blue-400" />
              <span>Revenue</span>
            </div>
            <ul className="space-y-2.5">
              {['Convert existing followers into paying members', 'Sell programmes, plans and premium content', 'Own the community instead of renting a feed', 'See what earns and what retains'].map((t, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-blue-100/70 leading-relaxed">
                  <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />{t}
                </li>
              ))}
            </ul>
          </div>

          <div className="p-6 sm:p-8 rounded-2xl bg-white/[0.06] border border-white/10">
            <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-4">For Users</p>
            <div className="flex flex-wrap items-center gap-2 mb-5 text-sm font-semibold text-white">
              <span>Programme</span><ArrowRight className="h-3.5 w-3.5 text-blue-400" />
              <span>Progress</span><ArrowRight className="h-3.5 w-3.5 text-blue-400" />
              <span>Habit</span>
            </div>
            <ul className="space-y-2.5">
              {['Complete structured programmes, not loose clips', 'Direct access to the creators they follow', 'Challenges that keep training social', 'Progress tracking and gamification'].map((t, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-blue-100/70 leading-relaxed">
                  <CheckCircle2 className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />{t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ── Business model ───────────────────────────────────────────────── */}
      <Section testId="invest-business-model">
        <Eyebrow>Business Model</Eyebrow>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1d3d] leading-tight">
          We succeed when creators earn.
        </h2>

        <p className="mt-4 text-sm md:text-base text-slate-500 leading-relaxed max-w-2xl">
          OnlyMass takes a share of what creators make. The more a creator contributes to the network, the
          smaller that share becomes — permanently.
        </p>

        <div className="mt-10 rounded-3xl border border-slate-200 bg-white p-5 sm:p-8" data-testid="invest-fee-ladder">
          <div className="flex items-center justify-between gap-4 mb-7">
            <p className="text-sm font-semibold text-[#0f1d3d]">Creator commission ladder</p>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
              <TrendingDown className="h-3.5 w-3.5" /> OnlyMass fee decreases
            </span>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                fee: 20, creator: 80, label: 'Standard', who: 'Default creator commission',
                tone: 'slate', bar: 'bg-slate-400', text: 'text-[#0f1d3d]', card: 'border-slate-200 bg-slate-50',
              },
              {
                fee: 15, creator: 85, label: 'Early adopter / 1 qualified referral', who: 'Early adopter creators, or after one qualified Invite & Earn referral',
                tone: 'blue', bar: 'bg-blue-500', text: 'text-blue-700', card: 'border-blue-500 bg-blue-50/50 border-2',
              },
              {
                fee: 5, creator: 95, label: 'Founder/Admin approved', who: 'Not the standard rate — individual approval only',
                tone: 'dashed', bar: 'bg-emerald-500', text: 'text-emerald-700', card: 'border-dashed border-slate-300 bg-white',
              },
            ].map((step, i) => (
              <div key={i} className={`relative rounded-2xl border p-5 ${step.card}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Step {i + 1}</p>
                <div className="flex items-baseline gap-1.5">
                  <span className={`text-4xl sm:text-5xl font-bold leading-none ${step.text}`}>{step.fee}</span>
                  <span className={`text-xl font-bold ${step.text}`}>%</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">OnlyMass fee</p>

                <div className="mt-4 h-2 rounded-full bg-slate-200/70 overflow-hidden" aria-hidden="true">
                  <div className={`h-full rounded-full ${step.bar}`} style={{ width: `${step.fee}%` }} />
                </div>

                <p className="mt-4 text-sm font-bold text-[#0f1d3d]">{step.creator}% to creator</p>
                <p className="text-xs font-semibold text-[#0f1d3d] mt-3 leading-tight">{step.label}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{step.who}</p>

                {i < 2 && (
                  <span className="hidden sm:flex absolute top-1/2 -right-[13px] z-10 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-slate-200 items-center justify-center" aria-hidden="true">
                    <ArrowRight className="h-3 w-3 text-slate-400" />
                  </span>
                )}
              </div>
            ))}
          </div>

          <p className="mt-6 text-xs text-slate-400">
            5% is a discretionary Founder/Admin-approved rate, not an advertised tier.
          </p>
        </div>

        <p className="mt-10 text-sm font-semibold text-[#0f1d3d]">Monetisation methods</p>
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { icon: Crown, title: 'Subscriptions', desc: 'Recurring creator memberships' },
            { icon: Dumbbell, title: 'Workout & nutrition programmes', desc: 'One-time programme purchases' },
            { icon: Lock, title: 'Premium content', desc: 'Paid and subscriber-only posts' },
            { icon: HeartHandshake, title: 'Tips', desc: 'Direct creator support on content' },
          ].map((it, i) => (
            <div key={i} className="p-4 rounded-2xl border border-slate-200 bg-slate-50">
              <it.icon className="h-[18px] w-[18px] text-blue-600 mb-3" />
              <p className="text-sm font-semibold text-[#0f1d3d] leading-tight">{it.title}</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>

        {/* Invite & Earn — highlighted */}
        <div className="mt-10 relative overflow-hidden rounded-3xl bg-[#0f1d3d] p-6 sm:p-9"
          data-testid="invest-invite-earn">
          <div className="absolute inset-0 opacity-70" aria-hidden="true"
            style={{ background: 'radial-gradient(55% 65% at 90% 0%, rgba(46,169,217,0.28) 0%, rgba(15,29,61,0) 70%)' }} />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.07] px-3 py-1.5 mb-5">
              <Gift className="h-3.5 w-3.5 text-blue-300" />
              <span className="text-[11px] sm:text-xs font-semibold text-blue-100 uppercase tracking-wider">Invite &amp; Earn</span>
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-white leading-snug max-w-2xl">
              Creators can earn a permanently lower platform fee by helping grow the network.
            </h3>

            <div className="mt-7 grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 p-5 rounded-2xl bg-white/[0.06] border border-white/10">
                <p className="text-sm text-blue-100/75 leading-relaxed">
                  When an invited creator qualifies by connecting Stripe and publishing the required creator
                  content, the referring creator's platform commission drops by 5 percentage points
                  permanently.
                </p>
                <p className="mt-5 text-xs text-blue-200/60">
                  Referral rewards are applied to the creator's effective commission rate and do not expire.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-white/[0.04] border border-dashed border-white/15">
                <p className="text-xs font-semibold text-blue-200 uppercase tracking-wider">Discretionary</p>
                <p className="mt-2 text-sm text-blue-100/75 leading-relaxed">
                  Founder/Admin-approved creator rates may go as low as 5%.
                </p>
                <p className="mt-3 text-xs text-blue-300/60 leading-relaxed">
                  5% is not the standard rate. It applies only on individual Founder/Admin approval.
                </p>
              </div>
            </div>

            <p className="mt-7 text-sm md:text-base text-white/85 leading-relaxed max-w-3xl border-l-2 border-blue-400/50 pl-4">
              Creator acquisition becomes part of the product itself: creators are financially rewarded for
              bringing other qualified creators onto OnlyMass.
            </p>
          </div>
        </div>
      </Section>

      {/* ── Current stage: pre-launch ────────────────────────────────────── */}
      <Section tint testId="invest-traction">
        <Eyebrow>Current Stage</Eyebrow>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1d3d] leading-tight max-w-2xl">
          Pre-launch. Product built. Distribution next.
        </h2>
        <p className="mt-4 text-sm md:text-base text-slate-500 leading-relaxed max-w-2xl">
          OnlyMass is currently in closed beta ahead of its mobile launch. Existing accounts are limited to
          founder-controlled accounts and a small group of invited beta testers used to validate product
          flows and functionality.
        </p>
        <p className="mt-5 text-base md:text-lg font-semibold text-[#0f1d3d]">
          This isn't a pitch for an idea — the platform is built and operational today.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="invest-milestones">
          {[
            'Web platform operational',
            'Mobile application built and approaching launch',
            'Creator subscriptions & payments',
            'Stripe Connect integrated',
            'Workout and nutrition programme monetisation',
            'Tipping',
            'Challenges & gamification',
            'Creator analytics',
            'AI Fitness Coach',
            'Invite & Earn referral system',
          ].map((t, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-slate-200">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-[#0f1d3d] leading-snug">{t}</p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-sm md:text-base text-slate-500 leading-relaxed max-w-3xl border-l-2 border-blue-500/40 pl-4">
          Public creator acquisition has not started yet. The €250K pre-seed round is intended primarily to
          move OnlyMass from product validation into distribution and market validation.
        </p>
      </Section>

      {/* ── Why now ──────────────────────────────────────────────────────── */}
      <Section dark testId="invest-why-now">
        <EyebrowLight>Why Now</EyebrowLight>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight max-w-3xl">
          The window is open. It won't stay open forever.
        </h2>
        <p className="mt-5 text-sm md:text-base text-blue-100/70 leading-relaxed max-w-3xl">
          Fitness creators are growing. Online coaching is becoming normal. AI is making software
          dramatically easier to build. And consumers are overwhelmed by fragmented fitness content.
          OnlyMass has the opportunity to establish the marketplace before the category becomes crowded.
        </p>

        {/* Four pillars */}
        <div className="mt-12 grid lg:grid-cols-2 gap-4 lg:gap-5" data-testid="invest-why-now-pillars">
          {/* 01 */}
          <div className="p-6 sm:p-7 rounded-2xl bg-white/[0.05] border border-white/10">
            <p className="text-[11px] font-bold tracking-[0.2em] text-blue-400/70">01</p>
            <h3 className="mt-3 text-base md:text-lg font-bold text-white leading-snug">
              Fitness creators are becoming businesses
            </h3>
            <div className="mt-5 flex items-baseline gap-3">
              <span className="text-4xl sm:text-5xl font-bold text-blue-300 leading-none tabular-nums">$480B</span>
            </div>
            <p className="mt-2 text-xs text-blue-200/60">Projected global creator economy by 2027</p>
            <p className="mt-4 text-sm text-blue-100/70 leading-relaxed">
              Creators are increasingly looking beyond sponsorships and social algorithms for direct,
              recurring income. OnlyMass gives fitness creators the infrastructure to monetise their
              audience directly.
            </p>
            <p className="mt-4 text-[11px] text-blue-300/40">Source: Goldman Sachs — Creator Economy</p>
          </div>

          {/* 02 */}
          <div className="p-6 sm:p-7 rounded-2xl bg-white/[0.05] border border-white/10">
            <p className="text-[11px] font-bold tracking-[0.2em] text-blue-400/70">02</p>
            <h3 className="mt-3 text-base md:text-lg font-bold text-white leading-snug">
              Fitness has already moved online
            </h3>
            <div className="mt-5 flex items-baseline gap-3">
              <span className="text-4xl sm:text-5xl font-bold text-blue-300 leading-none tabular-nums">345M+</span>
            </div>
            <p className="mt-2 text-xs text-blue-200/60">Fitness-app users globally</p>
            <p className="mt-4 text-sm text-blue-100/70 leading-relaxed">
              Online coaching, mobile programmes and creator-led fitness are no longer temporary
              behaviours. The audience already exists. The behaviour already exists.
            </p>
            <p className="mt-4 text-[11px] text-blue-300/40">Source: ACSM global fitness trends</p>
          </div>

          {/* 03 — prominent */}
          <div className="relative overflow-hidden p-6 sm:p-8 rounded-2xl bg-blue-500/[0.13] border border-blue-400/35">
            <div className="absolute inset-0 opacity-80" aria-hidden="true"
              style={{ background: 'radial-gradient(60% 70% at 100% 0%, rgba(46,169,217,0.22) 0%, rgba(15,29,61,0) 70%)' }} />
            <div className="relative">
              <p className="text-[11px] font-bold tracking-[0.2em] text-blue-300">03</p>
              <h3 className="mt-3 text-sm font-semibold text-blue-200/80">
                AI is collapsing the barrier to building software
              </h3>
              <p className="mt-4 text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight">
                Building gets easier.<br className="hidden sm:block" /> Distribution gets harder.
              </p>
              <p className="mt-5 text-sm text-blue-100/75 leading-relaxed">
                AI means more founders can launch more products, faster and cheaper than ever. The long-term
                advantage is increasingly not who can build the app, but who owns the creator relationships,
                community, brand and distribution first.
              </p>
              <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white border-l-2 border-blue-400 pl-3">
                This is why delaying OnlyMass carries real strategic risk.
              </p>
            </div>
          </div>

          {/* 04 */}
          <div className="p-6 sm:p-7 rounded-2xl bg-white/[0.05] border border-white/10">
            <p className="text-[11px] font-bold tracking-[0.2em] text-blue-400/70">04</p>
            <h3 className="mt-3 text-base md:text-lg font-bold text-white leading-snug">
              Consumers have too much fitness information, not too little
            </h3>
            <div className="mt-5 flex flex-wrap gap-2" aria-hidden="true">
              {['TikTok', 'Instagram', 'YouTube', 'PDFs', 'Coaching apps', 'WhatsApp groups'].map((t, i) => (
                <span key={i} className="rounded-lg border border-dashed border-white/20 bg-white/[0.04] px-2.5 py-1 text-xs text-blue-200/70">
                  {t}
                </span>
              ))}
            </div>
            <p className="mt-4 text-sm text-blue-100/70 leading-relaxed">
              There is endless fitness content — but it is fragmented.
            </p>
            <p className="mt-4 text-lg md:text-xl font-bold text-white leading-snug">
              More information is not the answer. Better structure is.
            </p>
            <p className="mt-3 text-sm text-blue-100/70 leading-relaxed">
              OnlyMass brings creators, programmes, challenges, communities and monetisation into one
              dedicated fitness ecosystem.
            </p>
          </div>
        </div>

        {/* Differentiation */}
        <div className="mt-12 rounded-3xl bg-white p-6 sm:p-9" data-testid="invest-differentiation">
          <h3 className="text-xl sm:text-2xl font-bold text-[#0f1d3d] leading-snug max-w-3xl">
            Most platforms make money from their users. OnlyMass is designed to make money with them.
          </h3>
          <div className="mt-7 grid sm:grid-cols-3 gap-4">
            {[
              { label: 'Free to join', copy: 'Users can access the platform and participate without being forced through a platform-wide paywall.' },
              { label: 'Creators decide what they monetise', copy: 'Free content can coexist with subscriptions, premium programmes, tips and paid experiences.' },
              { label: 'Creator-aligned economics', copy: 'OnlyMass earns when creators earn — with platform commissions that can decrease as creators contribute to growth.' },
            ].map((it, i) => (
              <div key={i} className="p-5 rounded-2xl bg-slate-50 border border-slate-200">
                <p className="text-[11px] font-bold uppercase tracking-wider text-blue-600">{it.label}</p>
                <p className="mt-2.5 text-sm text-slate-500 leading-relaxed">{it.copy}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Competitive positioning */}
        <div className="mt-6 p-6 sm:p-8 rounded-2xl bg-white/[0.05] border border-white/10 max-w-3xl">
          <h3 className="text-lg md:text-xl font-bold text-white leading-snug">
            The market is validated. The category is still open.
          </h3>
          <p className="mt-4 text-sm text-blue-100/70 leading-relaxed">
            Existing fitness monetisation platforms prove that consumers will pay fitness creators. OnlyMass
            is competing to build a broader fitness marketplace and community where joining the ecosystem is
            free and creator monetisation is embedded throughout the product rather than being the entry
            ticket.
          </p>
        </div>

        {/* Closing statement */}
        <div className="mt-12 relative overflow-hidden rounded-3xl border border-blue-400/25 bg-[#0b1528] p-7 sm:p-12"
          data-testid="invest-why-now-closing">
          <div className="absolute inset-0 opacity-90" aria-hidden="true"
            style={{ background: 'radial-gradient(55% 80% at 0% 100%, rgba(46,169,217,0.22) 0%, rgba(11,21,40,0) 70%)' }} />
          <div className="relative max-w-3xl">
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-snug">
              Technology can be replicated.
            </p>
            <p className="mt-3 text-xl sm:text-2xl lg:text-3xl font-bold text-blue-300/90 leading-snug">
              A creator network, brand, community and distribution engine are much harder to replicate.
            </p>
            <p className="mt-3 text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-snug">
              That is why we are moving now.
            </p>
            <p className="mt-7 text-sm md:text-base font-semibold text-blue-200/70">
              Build the network before the market gets crowded.
            </p>
          </div>
        </div>
      </Section>

      {/* ── Go-to-market ─────────────────────────────────────────────────── */}
      <Section tint testId="invest-gtm">
        <Eyebrow>Go-To-Market</Eyebrow>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1d3d] leading-tight max-w-2xl">
          Product risk reduced. Distribution is the next problem to solve.
        </h2>

        {/* Flywheel */}
        <div className="mt-10 rounded-2xl bg-white border border-slate-200 p-5 sm:p-8">
          <div className="flex items-center gap-2 mb-6">
            <Repeat className="h-4 w-4 text-blue-600" />
            <p className="text-sm font-semibold text-[#0f1d3d]">The growth loop</p>
          </div>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {[
              { n: '01', t: 'Creator acquisition' },
              { n: '02', t: 'Creators bring audiences' },
              { n: '03', t: 'Users join' },
              { n: '04', t: 'Engagement' },
              { n: '05', t: 'Creator revenue' },
            ].map((s, i) => (
              <div key={i} className="relative p-4 rounded-xl bg-slate-50 border border-slate-200">
                <span className="text-[10px] font-bold text-blue-500 tracking-wider">{s.n}</span>
                <p className="text-sm font-semibold text-[#0f1d3d] leading-tight mt-1.5">{s.t}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-blue-600">
            <Repeat className="h-3.5 w-3.5" /> Creator revenue attracts more creators — the loop compounds
          </div>
        </div>

        <p className="mt-10 text-sm font-semibold text-[#0f1d3d]">Initial channels</p>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { icon: Users, t: 'Creator partnerships' },
            { icon: Megaphone, t: 'UGC / short-form content' },
            { icon: Target, t: 'Performance acquisition' },
            { icon: MessageSquare, t: 'Community-led distribution' },
            { icon: HeartHandshake, t: 'Referral incentives' },
            { icon: Rocket, t: 'Launch campaigns' },
          ].map((c, i) => (
            <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-white border border-slate-200">
              <c.icon className="h-4 w-4 text-blue-600 shrink-0" />
              <p className="text-sm font-medium text-[#0f1d3d]">{c.t}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Raise ────────────────────────────────────────────────────────── */}
      <Section testId="invest-raise">
        <Eyebrow>The Raise</Eyebrow>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1d3d] leading-tight">
          {INVESTOR.round}
        </h2>
        <p className="mt-4 text-sm md:text-base text-slate-500 leading-relaxed max-w-2xl">
          To take OnlyMass from a built product to a validated and repeatable distribution engine.
        </p>

        <div className="mt-12">
          <p className="text-xs sm:text-sm font-semibold text-blue-600 uppercase tracking-[0.14em] mb-3">Use of funds</p>
          <h3 className="text-2xl sm:text-3xl font-bold text-[#0f1d3d] leading-tight">
            25% Product. 75% Go-to-Market.
          </h3>
          <p className="mt-4 text-base md:text-lg font-semibold text-[#0f1d3d] max-w-2xl">
            40% of the round is dedicated to creator-led distribution and UGC.
          </p>
          <p className="mt-4 text-sm md:text-base text-slate-500 leading-relaxed max-w-3xl">
            OnlyMass is a creator platform, so our primary distribution thesis is creator-led:
            partnerships, short-form UGC, creator seeding, paid amplification of winning content and
            systematic testing of the channels that convert.
          </p>

          {/* Product vs go-to-market split */}
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
            <div className="flex h-3 rounded-full overflow-hidden bg-slate-100" aria-hidden="true">
              <div className="h-full bg-[#0f1d3d]" style={{ width: '25%' }} />
              <div className="h-full bg-blue-500" style={{ width: '75%' }} />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
              <span className="inline-flex items-center gap-2 text-slate-600">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#0f1d3d]" /> Product &amp; infrastructure — 25% · €62,500
              </span>
              <span className="inline-flex items-center gap-2 text-slate-600">
                <span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Go-to-market — 75% · €187,500
              </span>
            </div>
          </div>

          {/* Line items */}
          <div className="mt-4 space-y-3" data-testid="invest-use-of-funds">
            {[
              { label: 'Creator partnerships & UGC', pct: 40, eur: '€100,000', accent: true },
              { label: 'Product & infrastructure', pct: 25, eur: '€62,500' },
              { label: 'Creator acquisition & onboarding', pct: 15, eur: '€37,500' },
              { label: 'Launch & performance distribution', pct: 15, eur: '€37,500' },
              { label: 'Community & referral growth', pct: 5, eur: '€12,500' },
            ].map((it, i) => (
              <div key={i}
                className={`p-5 rounded-2xl border ${it.accent ? 'border-blue-500 border-2 bg-blue-50/40' : 'border-slate-200 bg-white'}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-sm font-semibold text-[#0f1d3d]">{it.label}</p>
                  <p className="shrink-0 text-sm font-bold text-[#0f1d3d] tabular-nums">
                    {it.pct}% <span className="font-medium text-slate-400">·</span> {it.eur}
                  </p>
                </div>
                <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
                  <div className={`h-full rounded-full ${it.accent ? 'bg-blue-500' : 'bg-[#0f1d3d]'}`}
                    style={{ width: `${it.pct}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-baseline justify-between gap-4 px-5 py-4 rounded-2xl bg-[#0f1d3d]">
            <p className="text-sm font-semibold text-white">Total round</p>
            <p className="text-sm font-bold text-white tabular-nums">100% <span className="font-medium text-blue-300/60">·</span> €250,000</p>
          </div>
        </div>
      </Section>

      {/* ── What the round unlocks ───────────────────────────────────────── */}
      <Section dark testId="invest-unlocks">
        <EyebrowLight>12-Month Objectives</EyebrowLight>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight max-w-3xl">
          What the €250K round is designed to unlock
        </h2>
        <p className="mt-4 text-sm md:text-base text-blue-100/70 leading-relaxed max-w-3xl">
          From a built, pre-launch product to measurable creator monetisation, repeatable acquisition and
          referral-led growth.
        </p>
        <div className="mt-6 inline-flex items-start gap-2 rounded-xl border border-amber-300/25 bg-amber-300/[0.07] px-3.5 py-2.5 max-w-2xl">
          <Info className="h-4 w-4 text-amber-300/80 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-100/80 leading-relaxed">
            All figures below are <span className="font-semibold text-amber-100">targets</span>, not current
            traction and not guaranteed forecasts. OnlyMass is pre-launch and has no public traction today.
          </p>
        </div>

        {/* Timeline */}
        <div className="mt-10 relative">
          <div className="hidden lg:block absolute left-0 right-0 top-[38px] h-px bg-gradient-to-r from-white/10 via-blue-400/40 to-blue-400/70" aria-hidden="true" />
          <div className="grid lg:grid-cols-4 gap-4 lg:gap-5" data-testid="invest-timeline">
            {[
              {
                month: 'Month 3', title: 'Launch & Establish Baselines', strong: false,
                copy: 'Launch the mobile app, onboard the first meaningful creator cohort, begin structured UGC campaigns and establish initial CAC, activation and retention baselines.',
                metrics: [
                  ['Qualified creators', '100'], ['Registered users', '3,000'], ['MAU', '1,000'],
                  ['Monthly paying users', '150'], ['Monthly GMV', '€3,000'], ['Monthly OnlyMass revenue', '€300'],
                  ['UGC assets produced/tested', '40+'], ['Referral contribution', '10%'],
                ],
              },
              {
                month: 'Month 6', title: 'Validate', strong: false,
                copy: 'Identify the creator profiles, UGC formats and acquisition channels that convert best and begin validating repeatable unit economics.',
                metrics: [
                  ['Qualified creators', '300'], ['Registered users', '12,000'], ['MAU', '6,000'],
                  ['Monthly paying users', '900'], ['Monthly GMV', '€18,000'], ['Monthly OnlyMass revenue', '€1,800'],
                  ['UGC assets produced/tested', '120+'], ['Referral contribution', '15%'],
                ],
              },
              {
                month: 'Month 9', title: 'Scale Winners', strong: false,
                copy: 'Scale the best-performing creator partnerships and UGC, increase transaction volume and strengthen the Invite & Earn referral loop.',
                metrics: [
                  ['Qualified creators', '600'], ['Registered users', '28,000'], ['MAU', '14,000'],
                  ['Monthly paying users', '2,500'], ['Monthly GMV', '€50,000'], ['Monthly OnlyMass revenue', '€5,000'],
                  ['UGC assets produced/tested', '220+'], ['Referral contribution', '20%'],
                ],
              },
              {
                month: 'Month 12', title: 'Prove the Model', strong: true,
                copy: 'Demonstrate a repeatable creator-acquisition engine, meaningful transaction volume, retention and referral-led growth, positioning OnlyMass for its next institutional seed round.',
                metrics: [
                  ['Qualified creators', '1,000'], ['Monthly monetising creators', '~700'],
                  ['Registered users', '50,000'], ['MAU', '25,000'], ['Monthly paying users', '5,000'],
                  ['Monthly GMV', '€100,000'], ['Monthly OnlyMass platform revenue', '€10,000'],
                  ['UGC assets produced/tested', '300+'], ['Referral contribution', '25%+'],
                ],
              },
            ].map((stage, i) => (
              <div key={i} className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`relative z-10 w-4 h-4 rounded-full border-2 ${stage.strong ? 'bg-blue-400 border-[#0f1d3d] ring-4 ring-blue-400/20' : 'bg-[#0f1d3d] border-white/25'}`} aria-hidden="true" />
                  <span className={`text-sm font-bold ${stage.strong ? 'text-white' : 'text-blue-200/80'}`}>{stage.month}</span>
                </div>

                <div className={`h-full p-5 rounded-2xl border ${stage.strong ? 'bg-white/[0.11] border-blue-400/40 shadow-[0_0_0_1px_rgba(96,165,250,0.15)]' : 'bg-white/[0.05] border-white/10'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className={`text-base font-bold leading-snug ${stage.strong ? 'text-white' : 'text-blue-50/90'}`}>{stage.title}</h3>
                    <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-200">Target</span>
                  </div>

                  <dl className="mt-4 space-y-2">
                    {stage.metrics.map(([label, value], j) => (
                      <div key={j} className="flex items-baseline justify-between gap-3 border-b border-white/[0.06] pb-1.5 last:border-0">
                        <dt className="text-xs text-blue-200/60 leading-snug">{label}</dt>
                        <dd className={`text-sm font-bold tabular-nums shrink-0 ${stage.strong ? 'text-white' : 'text-blue-50/85'}`}>{value}</dd>
                      </div>
                    ))}
                  </dl>

                  <p className="mt-4 text-xs text-blue-200/60 leading-relaxed">{stage.copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Month-12 efficiency targets */}
        <div className="mt-12">
          <p className="text-sm font-semibold text-white">Month-12 efficiency targets</p>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="invest-efficiency-targets">
            {[
              { label: 'Qualified creator CAC', value: '≤ €50' },
              { label: 'Paying-user CAC', value: '≤ €15' },
              { label: 'Creator activation rate', value: '≥ 50%' },
              { label: 'User retention', value: '≥ 30% D30 / ≥ 20% D90' },
              { label: 'Creator retention', value: '≥ 60% D30 / ≥ 40% D90' },
            ].map((it, i) => (
              <div key={i} className="p-4 rounded-2xl bg-white/[0.05] border border-white/10">
                <p className="text-sm font-bold text-white leading-snug">{it.value}</p>
                <p className="text-xs text-blue-200/60 mt-1.5 leading-snug">{it.label}</p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-300/50 mt-2">Target</p>
              </div>
            ))}
          </div>
        </div>

        {/* Economics note */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-5 sm:p-6 max-w-3xl"
          data-testid="invest-economics-note">
          <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-3">How the Month-12 target adds up</p>
          <p className="text-sm text-blue-100/75 leading-relaxed">
            At a 10% average effective OnlyMass fee, €10K in monthly platform revenue corresponds to
            approximately €100K in monthly creator GMV. At the target mix, roughly 700 monetising creators
            would need to generate about €143 per month each.
          </p>
        </div>
      </Section>

      {/* ── Founder ──────────────────────────────────────────────────────── */}
      <Section tint testId="invest-founder">
        <Eyebrow>Founder</Eyebrow>
        <div className="mt-2 rounded-2xl bg-white border border-slate-200 p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#0f1d3d] flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-lg">AB</span>
            </div>
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-[#0f1d3d] leading-tight">Antonios Bairas</h3>
              <p className="text-sm text-slate-500 mt-0.5">Founder, OnlyMass</p>
            </div>
          </div>
          <p className="mt-5 text-sm md:text-base text-slate-500 leading-relaxed max-w-2xl">
            Built and financed OnlyMass from its earliest stage while leading product development, monetisation
            infrastructure, mobile development and launch preparation.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
            <a href={contactHref} onClick={() => handleContact('founder')} data-testid="invest-founder-email"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-[#0f1d3d] hover:bg-[#1a2d55] text-white text-sm font-semibold transition-colors">
              <Mail className="h-4 w-4" /> {INVESTOR.email}
            </a>

            <a href={INVESTOR.bookingUrl} target="_blank" rel="noopener noreferrer"
              onClick={() => handleBooking('founder')} data-testid="invest-founder-booking"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-slate-300 text-[#0f1d3d] text-sm font-semibold hover:bg-slate-50 transition-colors">
              <CalendarClock className="h-4 w-4" /> Book a Meeting
            </a>

            <a href={INVESTOR.deckUrl} target="_blank" rel="noopener noreferrer"
              onClick={() => handleDeck('founder')} data-testid="invest-founder-deck"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-slate-300 text-[#0f1d3d] text-sm font-semibold hover:bg-slate-50 transition-colors">
              <FileText className="h-4 w-4" /> View Investor Deck
            </a>
          </div>
        </div>
      </Section>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#0b1528] py-16 sm:py-24" data-testid="invest-final-cta">
        <div className="absolute inset-0 opacity-60" aria-hidden="true"
          style={{ background: 'radial-gradient(50% 60% at 80% 100%, rgba(46,169,217,0.22) 0%, rgba(11,21,40,0) 70%)' }} />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
            The fitness creator economy deserves its own platform.
          </h2>
          <p className="mt-4 text-sm md:text-base text-blue-200/70">OnlyMass — {INVESTOR.round}</p>

          <div className="mt-9 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
            <a href={INVESTOR.deckUrl} target="_blank" rel="noopener noreferrer"
              onClick={() => handleDeck('final')} data-testid="invest-final-deck-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl bg-white text-[#0f1d3d] text-sm sm:text-base font-semibold hover:bg-blue-50 active:scale-[0.98] transition-all">
              <FileText className="h-4 w-4 shrink-0" /> Investor Deck
            </a>
            <a href={INVESTOR.bookingUrl} target="_blank" rel="noopener noreferrer"
              onClick={() => handleBooking('final')} data-testid="invest-final-booking-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl border border-white/25 text-white text-sm sm:text-base font-semibold hover:bg-white/10 active:scale-[0.98] transition-all">
              <CalendarClock className="h-4 w-4 shrink-0" /> Book a Meeting
            </a>
            <a href={contactHref} onClick={() => handleContact('final')} data-testid="invest-final-contact-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl border border-white/25 text-white text-sm sm:text-base font-semibold hover:bg-white/10 active:scale-[0.98] transition-all">
              <Mail className="h-4 w-4 shrink-0" /> Contact Founder
            </a>
          </div>
          <p className="mt-8 text-xs text-blue-300/40">
            Full investor deck (16 slides, PDF) opens in a new tab. No sign-up required.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0b1528] border-t border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-6 h-6 rounded-md" />
            <span className="text-sm font-bold text-white">OnlyMass</span>
          </div>
          <p className="text-gray-600 text-xs">&copy; {new Date().getFullYear()} OnlyMass. All rights reserved.</p>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0f1d3d]/95 backdrop-blur-lg border-t border-white/10 px-4 py-3 flex gap-2.5"
        data-testid="invest-sticky-cta">
        <a href={INVESTOR.deckUrl} target="_blank" rel="noopener noreferrer"
          onClick={() => handleDeck('sticky')} data-testid="invest-sticky-deck-btn"
          className="flex-1 h-11 rounded-xl border border-white/25 text-white text-sm font-semibold inline-flex items-center justify-center active:scale-[0.98] transition-transform">
          Investor Deck
        </a>
        <a href={contactHref} onClick={() => handleContact('sticky')} data-testid="invest-sticky-contact-btn"
          className="flex-1 h-11 rounded-xl bg-white text-[#0f1d3d] text-sm font-semibold inline-flex items-center justify-center active:scale-[0.98] transition-transform">
          Contact Founder
        </a>
      </div>
      <div className="sm:hidden h-[68px]" aria-hidden="true" />
    </div>
  );
};

export default InvestPage;
